/**
 * Analytics Store Slice
 *
 * Zustand slice for usage analytics state management.
 * Handles analytics data, filters, and loading states.
 */

import { clearError as clearOpError, isOperationLoading } from '@gw/ui';

import { analyticsApi } from '@/api/analytics';
import { createAsyncSliceAction } from '@/stores/helpers/asyncSliceAction';
import type {
  AnalyticsFilters,
  DateRangePreset,
  UsageAnalytics,
  UsageAnalyticsQuery,
  UserUsageAnalyticsResponse,
  UserUsageAnalyticsParams,
  ApplicationUsageAnalyticsResponse,
  ApplicationUsageAnalyticsParams,
} from '@/types/analytics.types';

import type { ErrorStates, ErrorStatuses, LoadedTimestamps, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// GW-347: Date Range Preset Conversion Helper
// ============================================================================

/**
 * Convert a date range preset to actual start and end dates
 *
 * @param preset - The date range preset (last_7_days, last_30_days, last_90_days, custom)
 * @returns Object with start_date and end_date in ISO format (YYYY-MM-DD) or undefined for custom
 */
export function getDateRangeFromPreset(preset: DateRangePreset): {
  start_date: string | undefined;
  end_date: string | undefined;
} {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0]; // Today in YYYY-MM-DD format

  switch (preset) {
    case 'last_7_days': {
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
      return {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate,
      };
    }
    case 'last_30_days': {
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      return {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate,
      };
    }
    case 'last_90_days': {
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 90);
      return {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate,
      };
    }
    case 'custom':
    default:
      return {
        start_date: undefined,
        end_date: undefined,
      };
  }
}

// ============================================================================
// GW-428: Date Range Preset to Period Conversion Helper
// ============================================================================

/**
 * Convert a date range preset to API period parameter
 *
 * @param preset - The date range preset (last_7_days, last_30_days, last_90_days, custom)
 * @returns Period string for API (7d, 30d, 90d, all)
 */
export function getPeriodFromPreset(preset: DateRangePreset): '7d' | '30d' | '90d' | 'all' {
  switch (preset) {
    case 'last_7_days':
      return '7d';
    case 'last_30_days':
      return '30d';
    case 'last_90_days':
      return '90d';
    case 'custom':
    default:
      return 'all';
  }
}

// ============================================================================
// Slice Interface
// ============================================================================

export interface AnalyticsSlice {
  // State properties
  usageAnalytics: UsageAnalytics | null;
  userUsageAnalytics: UserUsageAnalyticsResponse | null; // GW-208: User-specific analytics
  applicationUsageAnalytics: ApplicationUsageAnalyticsResponse | null; // GW-301: Application-specific analytics
  filters: AnalyticsFilters;
  isLoading: LoadingStates;
  hasLoaded: LoadingStates; // Object tracking which operations have completed
  lastLoaded: LoadedTimestamps; // Object tracking timestamps of completed operations
  errors: ErrorStates; // Object tracking errors per operation
  errorStatuses: ErrorStatuses;

  // Actions
  fetchUsageAnalytics: (query?: UsageAnalyticsQuery) => Promise<void>;
  fetchUserUsageAnalytics: (
    userId: string,
    params?: Partial<UserUsageAnalyticsParams>
  ) => Promise<void>; // GW-208
  clearUserUsageAnalytics: () => void; // GW-208
  fetchApplicationUsageAnalytics: (
    appId: string,
    params?: Partial<ApplicationUsageAnalyticsParams>
  ) => Promise<void>; // GW-301
  clearApplicationUsageAnalytics: () => void; // GW-301
  updateFilters: (filters: Partial<AnalyticsFilters>) => void;
  resetFilters: () => void;
  clearError: (operation?: string) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: AnalyticsFilters = {
  date_range_preset: 'last_30_days',
  custom_start_date: null,
  custom_end_date: null,
  granularity: 'daily',
  application_id: null,
};

const initialState = {
  usageAnalytics: null,
  userUsageAnalytics: null, // GW-208
  applicationUsageAnalytics: null, // GW-301
  filters: initialFilters,
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  errorStatuses: {},
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createAnalyticsSlice: StateCreator<AnalyticsSlice, [], [], AnalyticsSlice> = (
  set,
  get
) => ({
  ...initialState,

  // ===========================================================================
  // Actions
  // ===========================================================================

  /**
   * Fetch usage analytics with current filters
   *
   * @param query - Optional query parameters. Merged on top of the filters-derived
   *   query rather than replacing it, so callers (e.g. the global_admin org
   *   selector) can override a single field like `org_id` without dropping the
   *   currently selected date range/granularity.
   *
   *   GW-8512: pass `org_id: null` explicitly to mean "All Organizations" —
   *   this is preserved through to the API client (which omits org_id from the
   *   request), unlike `org_id: undefined` which falls back to the current org.
   */
  fetchUsageAnalytics: async (query?: UsageAnalyticsQuery) => {
    const operation = 'fetch_usage_analytics';
    if (isOperationLoading(get().isLoading, operation)) return;

    // Build the base query from current filters
    const { filters } = get();

    // GW-347: Convert date range preset to actual dates
    let dateParams: { start_date?: string; end_date?: string };
    if (filters.date_range_preset === 'custom') {
      dateParams = {
        start_date: filters.custom_start_date || undefined,
        end_date: filters.custom_end_date || undefined,
      };
    } else {
      dateParams = getDateRangeFromPreset(filters.date_range_preset);
    }

    const baseQuery: UsageAnalyticsQuery = {
      granularity: filters.granularity,
      application_id: filters.application_id || undefined,
      ...dateParams,
    };

    // GW-8512: Merge caller-provided overrides on top of the filters-derived
    // base query instead of replacing it outright, so selecting an org (or
    // "All Organizations") does not silently discard the active date range.
    const analyticsQuery: UsageAnalyticsQuery = { ...baseQuery, ...query };

    await createAsyncSliceAction<AnalyticsSlice, UsageAnalytics>(
      operation,
      () => analyticsApi.getUsageAnalytics(analyticsQuery),
      (analytics) => ({ usageAnalytics: analytics }),
      set,
      get
    )();
  },

  /**
   * Update analytics filters
   *
   * @param filters - Partial filter updates
   */
  updateFilters: (filters: Partial<AnalyticsFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }));
  },

  /**
   * Reset filters to initial state
   */
  resetFilters: () => {
    set({ filters: initialFilters });
  },

  /**
   * Clear error for a specific operation
   *
   * @param operation - Operation key
   */
  clearError: (operation?: string) => {
    if (operation) {
      set((state) => ({
        errors: clearOpError(state.errors, operation),
      }));
    } else {
      set({ errors: {} });
    }
  },

  // ===========================================================================
  // GW-208: User-Specific Analytics Actions
  // ===========================================================================

  /**
   * Fetch user-specific usage analytics
   *
   * @param userId - User ID to fetch analytics for
   * @param params - Optional additional query parameters
   */
  fetchUserUsageAnalytics: async (userId: string, params?: Partial<UserUsageAnalyticsParams>) => {
    const operation = 'fetch_user_usage_analytics';
    if (isOperationLoading(get().isLoading, operation)) return;

    const { filters } = get();
    // GW-428: Convert date_range_preset to period for API
    const period = getPeriodFromPreset(filters.date_range_preset);
    const analyticsParams: UserUsageAnalyticsParams = {
      user_id: userId,
      granularity: params?.granularity || filters.granularity,
      period,
      ...params,
    };

    await createAsyncSliceAction<AnalyticsSlice, UserUsageAnalyticsResponse>(
      operation,
      () => analyticsApi.getUserUsageAnalytics(analyticsParams),
      (userAnalytics) => ({ userUsageAnalytics: userAnalytics }),
      set,
      get
    )();
  },

  /**
   * Clear user-specific analytics state and related errors
   */
  clearUserUsageAnalytics: () => {
    set((state) => ({
      userUsageAnalytics: null,
      errors: clearOpError(state.errors, 'fetch_user_usage_analytics'),
    }));
  },

  // ===========================================================================
  // GW-301: Application-Specific Analytics Actions
  // ===========================================================================

  /**
   * Fetch application-specific usage analytics
   *
   * @param appId - Application ID to fetch analytics for
   * @param params - Optional additional query parameters
   */
  fetchApplicationUsageAnalytics: async (
    appId: string,
    params?: Partial<ApplicationUsageAnalyticsParams>
  ) => {
    const operation = 'fetch_application_usage_analytics';
    if (isOperationLoading(get().isLoading, operation)) return;

    const { filters } = get();
    // GW-428: Convert date_range_preset to period for API
    const period = getPeriodFromPreset(filters.date_range_preset);
    const analyticsParams: ApplicationUsageAnalyticsParams = {
      app_id: appId,
      granularity: params?.granularity || filters.granularity,
      period,
      ...params,
    };

    await createAsyncSliceAction<AnalyticsSlice, ApplicationUsageAnalyticsResponse>(
      operation,
      () => analyticsApi.getApplicationUsageAnalytics(analyticsParams),
      (appAnalytics) => ({ applicationUsageAnalytics: appAnalytics }),
      set,
      get
    )();
  },

  /**
   * Clear application-specific analytics state and related errors
   */
  clearApplicationUsageAnalytics: () => {
    set((state) => ({
      applicationUsageAnalytics: null,
      errors: clearOpError(state.errors, 'fetch_application_usage_analytics'),
    }));
  },
});
